import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ValidationPatterns } from '../validation-patterns';
import { SsApiService } from '../../services/api/ss-api.service';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of, Subscription } from 'rxjs';
import { STORAGE_LESS_MODAL_SHOWED, UserService } from '../../services/user/user.service';
import { Router } from '@angular/router';
import { ToastMessageService } from '../../modules/toast-message/toast-message.service';
import { CacheService } from '../../services/cache/cache.service';
import { ContentUpdaterService } from '../../services/content-updater/content-updater.service';
import { FormsErrorHandlerService } from '../../services/forms-error-handler.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { LocalHistoryService } from '../../services/local-history.service';
import { WrSocketService } from '../../services/wr-socket.service';
import { GlobalEventsService } from '../../services/global-events.service';
import { inject, signal } from '@angular/core';
import { ModalsFromUrlService } from '../../modal-v2/modals-from-url.service';
import { LocalstorageService } from '../../services/localstorage.service';
import { UbidexService } from '../../services/ubidex.service';
import { registrationStorageName } from './register-form-controller';
import { StagDetectorService } from '../../services/stag-detector.service';
import { LOCAL_STORAGE_FRAUD_KEY_DEPOSIT } from '../../services/fraud-check.service';
import { ZendeskChatService } from '../../services/zendesk/zendesk-chat.service';
import { CFCacheService } from '../../services/cache-control.service';

/**
 * For handling login forms
 */
export class LoginFormController {

  /**
   * Access to global services
   */
  private _fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private _ssApi: SsApiService = inject(SsApiService);
  private _user: UserService = inject(UserService);
  private _router: Router = inject(Router);
  private _toastMessage: ToastMessageService = inject(ToastMessageService);
  private _cache: CacheService = inject(CacheService);
  private _cacheCF = inject(CFCacheService);
  private _contentUpdater: ContentUpdaterService = inject(ContentUpdaterService);
  private _formErrors: FormsErrorHandlerService = inject(FormsErrorHandlerService);
  private _gtm: GoogleTagManagerService = inject(GoogleTagManagerService);
  private _stag: StagDetectorService = inject(StagDetectorService);
  private _history: LocalHistoryService = inject(LocalHistoryService);
  private _modalsFromUrl: ModalsFromUrlService = inject(ModalsFromUrlService);
  private _wrSocket: WrSocketService = inject(WrSocketService);
  private _events: GlobalEventsService = inject(GlobalEventsService);
  private _storage: LocalstorageService = inject(LocalstorageService);
  private _ubidex: UbidexService = inject(UbidexService);
  private _zendesk: ZendeskChatService = inject(ZendeskChatService);


  /**
   * Is loading state
   */
  public loading = signal<boolean>(false);

  /**
   * Login for FormGroup
   */
  private _form: UntypedFormGroup = this._fb.group({
    email: [null, [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: [null, [Validators.required]]
  });

  private navigationEndSubs: Subscription;

  constructor() {}

  /**
   * Access to private properties from outside
   */
  get form(): UntypedFormGroup { return this._form; }

  /**
   * Returns FormControl by key
   *
   * @param key
   */
  input(key: string): AbstractControl {
    return this._form.get(key);
  }

  /**
   * Submit form handler
   */
  submitForm() {
    this._formErrors.applyFormErrors(this._form, null, true);

    if (this._form.invalid) {
      return;
    }
    this.loading.set(true);
    this._storage.clearItem(STORAGE_LESS_MODAL_SHOWED);

    this._ssApi.usersSignIn({
      user: this._form.value
    }).pipe(
      switchMap((resp) => this._cacheCF.updateCache$().pipe(map(data => resp))),
      tap(response => {
        this._cache.clear();
        this._user.applyUserInfo(response);
        this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);

        forkJoin([
          this._user.getUserAccounts(),
          this._user.getUserAccountsCompatibility(),
          this._user.getPlayerStats()
        ]).subscribe(() => {
          this._modalsFromUrl.controller.close();
          this.loading.set(false);
          this._processRedirectAfterLogin();
          this.navigationEndSubs = this._events.routerNavigationEnd$.pipe(
            tap((e) => {
              if (this.navigationEndSubs && !e.url.includes('login')) {
                this.navigationEndSubs.unsubscribe();
              }
            })
          ).subscribe();
        });

        this._gtm.signInSuccess(response.id);

      }),
      switchMap(() => this._stag.resolveStagParam$()),
      tap(async () => {
        this._contentUpdater.updateAll();
        this._wrSocket.sendEventLogin();
      }),
      catchError(error => {
        this._toastMessage.error(this._formErrors.ssBackendErrorsToArray(error.error)[0] || 't.invalid-email-password');
        this._changesFieldErrorsIfSSError(error.error);
        this._formErrors.applyFormErrors(this._form, error.error, true);
        this.loading.set(false);
        return of(error);
      }),
      finalize(() => {
        this._ubidex.onLogin();
        this._zendesk.setInitFunctionalData();
      })
    ).subscribe();
  }

  /**
   * Resolve correct url fore redirect after login and navigate
   *
   * @private
   */
  private async _processRedirectAfterLogin() {
    const beforeLoginUrl = this._history.getUrl('promo-auth');

    let urlToRedirect: any[] = beforeLoginUrl ? [beforeLoginUrl.url] : ['/'];

    if (this._user.currentCurrency.amount <= this._user.currentCurrencyToEquivalent && !beforeLoginUrl) {
      urlToRedirect = ['/profile/deposit', {data: {context: 'deposit'}}];
    }

    this._router.navigate(urlToRedirect).then(() => {
      this._storage.clearItem(registrationStorageName);
      this._toastMessage.success('t.auth-success');
    });
  }

  /**
   * If error from SS reset errors for some fields if valueChanges
   * @private
   */
  private _changesFieldErrorsIfSSError(errors) {
    const controls = Object.keys(errors.errors);

    if (controls.length) {
      this.form.valueChanges.pipe(
        tap(() => {
          controls.forEach(control => this.input(control).setErrors(null));
          this._formErrors.applyFormErrors(this._form, null, true);
        }),
      ).subscribe();
    }

  }
}
